import React, { useState } from 'react';
import { getWebsiteUnits, getContact, isSold, getUnitName, getPageContent } from './websiteStyles';

const DARK = '#14161C';
const PAPER = '#FAFAF8';
const PAPER2 = '#F3F3F1';
const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

function WebsiteUnitDetail({ unit, onBack, onNavigate, onViewUnit }) {
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [tab, setTab] = useState('overview');
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  const contact = getContact();
  const cms = getPageContent('website_inventory');
  const name = getUnitName(unit);
  const sold = isSold(unit);
  const reserved = unit.status === 'reserved';
  const images = unit.images || [];
  const isVehicle = unit._type === 'vehicle' && !unit.cutType;

  const waMsg = encodeURIComponent(`Hi, I'm interested in ${name}${unit.sku ? ' (' + unit.sku + ')' : ''}. Can you share pricing, condition details, and shipping options?`);

  // Similar units
  const allUnits = getWebsiteUnits();
  const similar = allUnits.filter(u => u.id !== unit.id && u.make === unit.make && !isSold(u)).slice(0, 4);

  const isPart = !isVehicle;

  // Specs for overview
  const quickSpecs = isPart ? [
    ['Category', unit.category || unit.cutType || null],
    ['Condition', unit.condition || null],
    ['Fits', unit.make && unit.model ? `${unit.make} ${unit.model}` : null],
    ['Year', unit.year || null],
    ['Fuel', unit.fuel || unit.fuelType || null],
    ['Mileage', unit.mileage ? Number(unit.mileage).toLocaleString() + ' km' : null],
    ['Source', unit.chassis ? unit.chassis.substring(0, 5) + '***' : null],
    ['Stock', unit.sku || null],
  ].filter(([, v]) => v) : [
    ['Power', unit.power || unit.displacement || null],
    ['Torque', unit.torque || null],
    ['0-100', unit.acceleration || null],
    ['Drive', unit.driveSystem || unit.drive || null],
    ['Fuel', unit.fuel || unit.fuelType || null],
    ['Trans', unit.transmission || null],
    ['Body', unit.bodyType || unit.category || null],
    ['KM', unit.mileage ? Number(unit.mileage).toLocaleString() : null],
  ].filter(([, v]) => v);

  // Full specs for specifications tab
  const engineSpecs = [
    ['Engine', unit.displacement || unit.engineCode || null],
    ['Power', unit.power || null],
    ['Torque', unit.torque || null],
    ['0-100', unit.acceleration || null],
    ['Top Speed', unit.topSpeed || null],
  ].filter(([, v]) => v);

  const drivetrainSpecs = [
    ['Drivetrain', unit.driveSystem || unit.drive || null],
    ['Transmission', unit.transmission || null],
    ['Fuel', unit.fuel || unit.fuelType || null],
    ['Tank Capacity', unit.tankCapacity || null],
  ].filter(([, v]) => v);

  const bodySpecs = [
    ['Body', unit.bodyType || unit.category || null],
    ['Seats', unit.seats || null],
    ['Color', unit.color || null],
    ['Steering', unit.steer || unit.steering || null],
  ].filter(([, v]) => v);

  const idSpecs = [
    ['VIN', unit.chassis ? unit.chassis.substring(0, 5) + '***' : null],
    ['Stock No.', unit.sku || null],
    ['Year', unit.year || null],
    ['Specifications', unit.specs || null],
    ['Mileage', unit.mileage ? Number(unit.mileage).toLocaleString() + ' km' : null],
    ['Grade', unit.grade || unit.score || null],
  ].filter(([, v]) => v);

  const specRow = (label, value) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${LINE}` }}>
      <span style={{ fontFamily: mono, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK3 }}>{label}</span>
      <span style={{ fontFamily: heading, fontSize: 14, fontWeight: 500, color: INK, textAlign: 'right' }}>{value}</span>
    </div>
  );

  const tabStyle = (active) => ({
    padding: '12px 0', fontSize: 15, fontWeight: active ? 600 : 400, color: active ? INK : INK3,
    background: 'none', border: 'none', borderBottom: active ? `2px solid ${INK}` : '2px solid transparent',
    cursor: 'pointer', fontFamily: heading, marginRight: isMobile ? 16 : 24, transition: 'all 0.2s',
  });

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: isMobile ? '12px 16px' : '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: heading, fontSize: isMobile ? 11 : 13, color: INK3, flexWrap: 'wrap' }}>
          <button onClick={() => onNavigate('website')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: INK3, fontFamily: heading, fontSize: 13 }}>Home</button>
          <span>›</span>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: INK3, fontFamily: heading, fontSize: 13 }}>
            {isVehicle ? 'Used cars' : 'Spare Parts'}
          </button>
          <span>›</span>
          <span style={{ color: INK }}>{name}</span>
        </div>
      </div>

      {/* Main content */}
      <section style={{ maxWidth: 1440, margin: '0 auto', padding: isMobile ? '8px 16px 32px' : '8px 24px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: isMobile ? 20 : 40, alignItems: 'flex-start' }} className="ws-detail-grid">
          {/* Left — Gallery */}
          <div>
            {/* Main image */}
            <div style={{ position: 'relative', borderRadius: isMobile ? 10 : 16, overflow: 'hidden', background: DARK, aspectRatio: isMobile ? '16/9' : '4/3', marginBottom: isMobile ? 8 : 12, cursor: 'pointer', maxHeight: isMobile ? 250 : 'none' }}
              onClick={() => images.length > 0 && setLightbox(activeImg)}>
              {images.length > 0 ? (
                <img src={images[activeImg]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', background: DARK }} />
              ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64, opacity: 0.2 }}>
                  {isVehicle ? '🚗' : '🔧'}
                </div>
              )}
              {/* Badges overlay */}
              <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 6 }}>
                {unit.is_featured && <span style={{ padding: '5px 12px', borderRadius: 6, background: '#DCFCE7', color: '#16A34A', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>● Featured</span>}
                {reserved && <span style={{ padding: '5px 12px', borderRadius: 6, background: '#FEF3C7', color: '#D97706', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Reserved</span>}
                {sold && <span style={{ padding: '5px 12px', borderRadius: 6, background: '#FEE2E2', color: '#DC2626', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sold</span>}
              </div>
              {/* Color + spec labels */}
              <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 6 }}>
                {unit.color && <span style={{ padding: '4px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', fontFamily: mono, fontSize: 9, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>ING · {unit.color}</span>}
              </div>
              {/* Image counter */}
              {images.length > 1 && (
                <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', padding: '4px 10px', borderRadius: 9999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', fontFamily: mono, fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
                  {activeImg + 1} / {images.length}
                </div>
              )}
              {/* Nav arrows */}
              {images.length > 1 && activeImg > 0 && (
                <button onClick={e => { e.stopPropagation(); setActiveImg(activeImg - 1); }} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 18 }}>‹</button>
              )}
              {images.length > 1 && activeImg < images.length - 1 && (
                <button onClick={e => { e.stopPropagation(); setActiveImg(activeImg + 1); }} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 18 }}>›</button>
              )}
            </div>
            {/* Thumbnails */}
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {images.map((img, i) => (
                  <div key={i} onClick={() => setActiveImg(i)} style={{
                    width: isMobile ? 56 : 72, height: isMobile ? 42 : 54, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                    border: i === activeImg ? `2px solid ${INK}` : `2px solid transparent`,
                    opacity: i === activeImg ? 1 : 0.6, transition: 'all 0.2s',
                  }}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right — Info */}
          <div>
            {/* SKU + Spec label */}
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.1em', color: INK3, textTransform: 'uppercase', marginBottom: 8, display: 'flex', gap: 12 }}>
              {unit.sku && <span>{unit.sku}</span>}
              {unit.specs && <span>· {unit.specs} spec</span>}
            </div>
            {/* Status badge */}
            {!sold && !reserved && (
              <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 6, background: '#DCFCE7', color: '#16A34A', fontFamily: mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Ready at yard</span>
            )}
            {/* Year + Make */}
            <div style={{ fontFamily: heading, fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: BLUE, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
              {isPart ? (unit.category || unit.cutType || 'Part') : `${unit.year} ${unit.make}`}
            </div>
            {/* Model / Part name */}
            <h1 style={{ fontFamily: heading, fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700, color: INK, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 20 }}>
              {isPart ? (unit.partName || unit.engineName || `${unit.make || ''} ${unit.model || ''}`.trim() || 'Part') : (unit.model || '')}
            </h1>

            {/* Pricing note */}
            <div style={{ padding: isMobile ? '12px 14px' : '16px 18px', borderRadius: 10, background: PAPER2, marginBottom: isMobile ? 14 : 20 }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: INK4 }}></span>
                {cms.pricing_note_title || 'Pricing on inquiry'}
              </div>
              <div style={{ fontSize: 13, color: INK2, lineHeight: 1.6 }}>
                {cms.pricing_note_text || 'Tailored quote with VAT, export & shipping in one message. Trade rates available with verified license.'}
              </div>
            </div>

            {/* CTA buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexDirection: isMobile ? 'column' : 'row' }}>
              <a href={`https://wa.me/${contact.whatsapp}?text=${waMsg}`} target="_blank" rel="noopener noreferrer"
                style={{ flex: isMobile ? 'none' : 1, width: isMobile ? '100%' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: isMobile ? '12px 16px' : '14px 20px', borderRadius: 10, background: GREEN, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none', fontFamily: heading, boxSizing: 'border-box' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', opacity: 0.6 }}></span>
                WhatsApp inquiry
              </a>
              <a href={`tel:${contact.phone}`}
                style={{ flex: isMobile ? 'none' : 1, width: isMobile ? '100%' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: isMobile ? '12px 16px' : '14px 20px', borderRadius: 10, background: INK, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none', fontFamily: heading, boxSizing: 'border-box' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" style={{ width: 16, height: 16 }}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                Call sales
              </a>
            </div>
            <a href={`mailto:${contact.email}?subject=${encodeURIComponent('Inquiry: ' + name)}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 20px', borderRadius: 10, border: `1px solid ${LINE}`, background: '#fff', color: INK, fontSize: 14, fontWeight: 500, textDecoration: 'none', fontFamily: heading, marginBottom: 24 }}>
              Send written inquiry <span>→</span>
            </a>

            {/* Quick specs */}
            {quickSpecs.length > 0 && (
              <div>
                <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 12 }}>Quick Specs</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0 }}>
                  {quickSpecs.map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${LINE}`, paddingRight: 16 }}>
                      <span style={{ fontFamily: mono, fontSize: 11, color: INK3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                      <span style={{ fontFamily: heading, fontSize: 14, fontWeight: 500, color: INK }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ Tabs section ═══ */}
      <section style={{ borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${LINE}` }}>
            <button onClick={() => setTab('overview')} style={tabStyle(tab === 'overview')}>Overview</button>
            <button onClick={() => setTab('specifications')} style={tabStyle(tab === 'specifications')}>Specifications</button>
          </div>

          {/* Tab content */}
          <div style={{ padding: '40px 0 60px' }}>
            {tab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: isMobile ? 20 : 40 }} className="ws-detail-grid">
                <div>
                  {unit.description ? (
                    <>
                      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 20 }}>
                        {unit.description.substring(0, 80)}{unit.description.length > 80 ? '...' : ''}
                      </h2>
                      <p style={{ fontSize: 15, color: INK2, lineHeight: 1.8 }}>{unit.description}</p>
                    </>
                  ) : (
                    <>
                      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 20 }}>
                        {name} — {sold ? 'previously' : 'currently'} available at our yard.
                      </h2>
                      <p style={{ fontSize: 15, color: INK2, lineHeight: 1.8 }}>
                        {isVehicle ? 'Full inspection completed. Contact us for detailed condition report, photos, and tailored pricing.' : 'Genuine OEM part. Contact us for fitment verification and pricing.'}
                      </p>
                    </>
                  )}
                  {/* Highlights */}
                  {unit.highlights && unit.highlights.length > 0 && (
                    <div style={{ marginTop: 32 }}>
                      <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 700, color: INK, marginBottom: 16 }}>Highlights</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                        {unit.highlights.map((h, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: INK2 }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke={INK3} strokeWidth="2" style={{ width: 16, height: 16, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                            {h}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'specifications' && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 20 : 48 }} className="ws-detail-grid">
                <div>
                  {engineSpecs.length > 0 && (
                    <div style={{ marginBottom: 40 }}>
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 16 }}>Engine</div>
                      {engineSpecs.map(([l, v]) => specRow(l, v))}
                    </div>
                  )}
                  {bodySpecs.length > 0 && (
                    <div>
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 16 }}>Body & Dimensions</div>
                      {bodySpecs.map(([l, v]) => specRow(l, v))}
                    </div>
                  )}
                </div>
                <div>
                  {drivetrainSpecs.length > 0 && (
                    <div style={{ marginBottom: 40 }}>
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 16 }}>Drivetrain</div>
                      {drivetrainSpecs.map(([l, v]) => specRow(l, v))}
                    </div>
                  )}
                  {idSpecs.length > 0 && (
                    <div>
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK3, marginBottom: 16 }}>Identification</div>
                      {idSpecs.map(([l, v]) => specRow(l, v))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ Similar units ═══ */}
      {similar.length > 0 && (
        <section style={{ background: PAPER2, borderTop: `1px solid ${LINE}` }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: isMobile ? '32px 16px 48px' : '48px 24px 64px' }}>
            <h3 style={{ fontFamily: heading, fontSize: 24, fontWeight: 700, color: INK, letterSpacing: '-0.03em', marginBottom: 24 }}>Similar units</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {similar.map(u => {
                const n = getUnitName(u);
                const img = u.images?.[0];
                return (
                  <div key={u.id} onClick={() => onViewUnit(u)} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${LINE}`, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.07)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ aspectRatio: '4/3', background: PAPER2, overflow: 'hidden' }}>
                      {img ? <img src={img} alt={n} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, opacity: 0.2 }}>🚗</div>}
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                      <div style={{ fontFamily: mono, fontSize: 10, color: INK3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{[u.year, u.make].filter(Boolean).join(' · ')}</div>
                      <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK }}>{u.model || n}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══ Lightbox ═══ */}
      {lightbox !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLightbox(null)}>
          <button style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', zIndex: 10 }} onClick={() => setLightbox(null)}>✕</button>
          {lightbox > 0 && <button style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer', width: 48, height: 48, borderRadius: '50%' }} onClick={e => { e.stopPropagation(); setLightbox(lightbox - 1); }}>‹</button>}
          {lightbox < images.length - 1 && <button style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer', width: 48, height: 48, borderRadius: '50%' }} onClick={e => { e.stopPropagation(); setLightbox(lightbox + 1); }}>›</button>}
          <img src={images[lightbox]} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
          <div style={{ position: 'absolute', bottom: 20, fontFamily: mono, color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{lightbox + 1} / {images.length}</div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) { .ws-detail-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

export default WebsiteUnitDetail;
